import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = 'RurayHUB';
const SITE_URL = 'https://ruraydigital.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/ruray-mark-v2.png`;

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Agente de IA en WhatsApp para clínicas dentales`;
  const fullUrl = `${SITE_URL}${path}`;
  const metaDescription = description || 'RurayHUB responde el WhatsApp de tu clínica dental 24/7, agenda citas y envía recordatorios automáticos. Deja de perder pacientes por mensajes sin responder.';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullUrl} />

      {/* hreflang: el sitio sirve es/en/pt en la misma URL (switcher client-side, no
          rutas por idioma), así que las 3 versiones se auto-referencian a la misma
          dirección — mismo patrón que ya usa sitemap.xml para la home */}
      <link rel="alternate" hrefLang="es" href={fullUrl} />
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="pt" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_LA" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
